<template>
  <div class="container admin-page-with-back-py">
    <div class="mb-2">
      <button type="button" class="p-0 back-button" style="border: none; background: transparent;"
              @click="handleBackButton">
        <BackIcon/>
        Back
      </button>
    </div>

    <div class="row">
      <div class="col-12 col-lg-8 offset-lg-2">
        <div class="row align-items-end mb-5">
          <div class="col col-auto">
            <user-avatar
                :avatarURL="teamMember.avatar_src"
                size="134"
            />
          </div>

          <div class="col">
            <div class="row">
              <div class="col">
                <div class="admin-staff-name">{{ teamMember.name }}</div>
                <div class="admin-staff-description">{{ teamMember.designation }}</div>
              </div>
            </div>

            <div class="row align-items-end" style="margin-top: -18px;">
              <div class="col">
                <div class="admin-staff-email">{{ teamMember.email }}</div>
              </div>
              <div class="col col-auto text-right">
                <div class="stat-unit font-weight-bold text-right" style="font-size: 16px;">
                  <span v-if="startDate === endDate">Today</span>
                  <span class="filter-subtitle" v-else>{{ formatDateForDisplay(this.startDate) }}</span>
                  <span class="filter-subtitle">- {{ formatDateForDisplay(this.endDate) }}</span>
                </div>
                <div class="admin-staff-cost">{{ paymentCurrency }} <span>{{ totalCost }}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-5">
          <div class="col-12 col-md-4">
            <stats-box
                name="Hours Spent"
                :number="totalTimeSpent"
                unit="In total"
            ></stats-box>
          </div>
          <div class="col-12 col-md-4">
            <stats-box
                name="Completed Tasks"
                :number="totalCompletedTasks"
                unit="In total"
            ></stats-box>
          </div>
          <div class="col-12 col-md-4">
            <stats-box
                name="Incomplete Tasks"
                :number="totalPendingTasks"
                unit="In total"
            ></stats-box>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <table class="wfh-table">
              <thead>
              <tr>
                <td>Title</td>
                <td>Spent</td>
                <td>Estimated</td>
                <td>Cost</td>
                <td>Status</td>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(task, key) of tasks"
                  :key="`completed-${key}`"
                  style="cursor:pointer;"
                  @click="viewTaskDetails(task.detailsPageUrl)"
              >
                <td>
                  <span>{{ task.title }}</span>
                  <span class="d-block task-sub" v-if="task.totalScreenshots > 0">
                    <span style="margin-right: 8px;">
                      <TaskIcon/>
                    </span>
                    <span class="font-weight-bold" style="margin-right: 4px;">{{ task.totalScreenshots }}</span> screenshots
                  </span>
                </td>
                <td style="white-space: nowrap;">{{ task.totalTime }}</td>
                <td style="white-space: nowrap;">{{ task.estimatedTime }}</td>
                <td style="white-space: nowrap;">
                  <div>{{ paymentCurrency }}{{ task.totalCost }}</div>
                </td>
                <td>
                  <task-status-badge :status="task.status"></task-status-badge>
                </td>
              </tr>

              <tr v-if="tasks.length === 0 && loading === false">
                <td colspan="5" class="text-center">No Tasks</td>
              </tr>

              <tr v-if="tasks.length === 0 && loading === true">
                <td colspan="5" class="text-center">
                  <div v-if="loading" class="d-block position-relative">
                    <p class="d-flex">
                      <span class="spinner-border mr-2" role="status">
                        <span class="sr-only">Loading...</span>
                      </span>
                      Loading tasks...
                    </p>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>

            <div class="row" v-if="hasMoreTasks">
              <div class="col-12 col-sm-5 mx-auto">
                <button
                    @click="fetchMoreTasks"
                    type="button"
                    class="wfh-btn-color-primary wfh-btn-large btn-block"
                >
                  See More
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BackIcon from '@icons/BackIcon';
import { get as getUserSummary } from '../../../portal/services/ReportService';
import minutesToHoursFormatter from '../../../utils/minutesToHoursFormatter';
import TaskIcon from '../../../icons/Tasks/TaskIcon';
import { mapState } from 'vuex';

export default {
  components: { TaskIcon, BackIcon },

  props: {
    teamMember: {
      required: true,
    },
  },

  data () {
    return {
      totalCost: 0,
      totalCompletedTasks: 'N/A',
      totalPendingTasks: 'N/A',
      tasks: [],
      reset: false,
      startDate: null,
      endDate: null,
      currentPage: 1,
      totalPages: 1,
      totalTimeSpent: 'N/A',
      companyId: null,
      paymentCurrency: null,
      loading: true,
    };
  },

  computed: {
    ...mapState(['currentUser']),

    hasMoreTasks () {
      return this.currentPage < this.totalPages;
    },
  },

  watch: {
    currentUser () {
      if (this.currentUser.company) {
        this.companyId = this.currentUser.company.id;
      }

      this.fetchTasks();
    },
  },

  methods: {
    minutesToHoursFormatter,

    handleBackButton () {
      window.history.back();
    },

    fetchMoreTasks () {
      this.currentPage++;
      this.fetchTasks();
    },

    async fetchTasks () {
      this.loading = true;
      const tasksResponse = await axios.get('/reports/tasks', {
        params: {
          is_personal: true,
          page: this.currentPage,
          start_date: this.startDate,
          end_date: this.endDate,
          per_page: 10,
          company_id: this.companyId,
          user_id: this.teamMember.id,
        },
      });

      const tasks = tasksResponse.data.data.map((task) => {
        const estimatedMinutes = task.task_cost ? (
            task.task_cost.hour_estimation ? task.task_cost.hour_estimation * 60 : 0
        ) : 0;

        return {
          date: moment(task.completed_at ?? task.combined_created_at).format('DD MMM YYYY'),
          id: task.id,
          id_hash: task.id_hash,
          title: task.title,
          totalTime: this.minutesToHoursFormatter(task.total_minutes_spent),
          estimatedTime: this.minutesToHoursFormatter(estimatedMinutes),
          totalCost: task.client_cost,
          status: !task.completed && task.total_minutes_spent > 0 ? 'on-going' : task.completed ? 'completed' : 'pending',
          detailsPageUrl: route('admin.finance-report.task-details', {
            hashedTaskId: task.id_hash,
            start_date: this.startDate,
            end_date: this.endDate,
          }),
          totalScreenshots: task.total_screenshots ?? 0,
        };
      });

      if (this.reset === true) {
        this.tasks = tasks;
      } else {
        this.tasks = [
          ...this.tasks,
          ...tasks,
        ];
      }

      this.totalPages = tasksResponse.data.last_page;
      this.reset = false;
      this.loading = false;
    },

    async fetchTeamMemberSummary () {
      const res = await getUserSummary({
        start_date: this.startDate,
        end_date: this.endDate,
        company_id: this.teamMember.company.id,
        user_id: this.teamMember.id,
      });
      const data = res.data;

      this.totalCost = data.payment.client_cost;
      this.totalTimeSpent = this.minutesToHoursFormatter(data.time.minutes_spent_on_tasks);
      this.totalCompletedTasks = data.meta.total_completed_tasks;
      this.totalPendingTasks = data.meta.total_pending_tasks;
      this.paymentCurrency = data.payment.currency === 'MYR' ? 'RM' : data.payment.currency;
    },

    setDefaultDateRange () {
      this.startDate = localStorage.getItem('wfh_adm_sd') ?? moment().startOf('month').format('YYYY-MM-DD');
      this.endDate = localStorage.getItem('wfh_adm_ed') ?? moment().format('YYYY-MM-DD');
    },

    formatDateForDisplay (date) {
      return moment(date).format('DD/MM/YYYY');
    },

    viewTaskDetails (link) {
      window.location.href = link;
    },
  },

  mounted () {
    this.setDefaultDateRange();
    this.fetchTeamMemberSummary();
  },
};
</script>

<style scoped lang="scss">
.admin-staff-name {
  color: #333;
  font-family: "Open Sans", sans-serif;
  font-size: 32px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 43px;
}

.admin-staff-description {
  color: #8392a6;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 24px;
}

.admin-staff-email {
  color: #303030;
  font-family: ".SF NS Display", sans-serif;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 22px;
}

.admin-staff-cost {
  color: #222227;
  font-family: "Open Sans", sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 33px;

  span {
    font-size: 48px;
    line-height: 65px;
  }
}

.task-sub {
  color: #8997aa;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  letter-spacing: 0;
  line-height: 22px;
}

</style>
